import React from 'react';
import { HiOutlineChatAlt2, HiOutlineRefresh } from 'react-icons/hi';
import { fromNow } from '../../helpers/utils/date-util';
import { safeString } from '../../helpers/utils/text-util';

/**
 * Conversation history rail.
 *
 * Modelled on the platform's sessions list, but read-only and guest-facing:
 * no pager (the rail scrolls), and entries may come either from the sessions
 * API or from this browser's local index - see `services/session`.
 */
export default function SessionsList({
  sessions,
  selectedSessionId,
  onSelectSession,
  onRefresh,
  loading,
  unauthorized,
}) {
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex items-center justify-between px-3 pb-1.5 pt-3">
        <h2 className="text-[11px] font-medium uppercase tracking-wider text-ink-faint">
          Recents
        </h2>
        <button
          type="button"
          onClick={onRefresh}
          title="Refresh conversations"
          aria-label="Refresh conversations"
          className="rounded p-1 text-ink-faint transition-colors hover:bg-elevated hover:text-ink"
        >
          <HiOutlineRefresh size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
        {sessions.length > 0 && (
          <ul className="space-y-0.5">
            {sessions.map(session => {
              const selected = session.sessionId === selectedSessionId;
              const preview = safeString(session.displayMessage);

              return (
                <li key={session.sessionId}>
                  <button
                    type="button"
                    onClick={() => onSelectSession(session)}
                    title={preview || session.sessionId}
                    className={`flex w-full items-start gap-2 rounded-lg px-2 py-1.5 text-left transition-colors ${
                      selected ? 'bg-elevated' : 'hover:bg-surface'
                    }`}
                  >
                    <HiOutlineChatAlt2
                      size={14}
                      className={`mt-0.5 flex-none ${selected ? 'text-ink' : 'text-ink-faint'}`}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block truncate text-[13px] leading-5 ${
                          selected ? 'text-ink' : 'text-ink-muted'
                        }`}
                      >
                        {preview || 'New conversation'}
                      </span>
                      {session.modifiedAt && (
                        <span className="mt-0.5 block truncate text-[11px] text-ink-faint">
                          {fromNow(session.modifiedAt)}
                        </span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {!loading && sessions.length === 0 && (
          <div className="px-2 py-6 text-center">
            <HiOutlineChatAlt2 size={18} className="mx-auto text-ink-faint" />
            <p className="mt-2 text-[12px] text-ink-muted">No conversations yet</p>
            <p className="mt-0.5 text-[11px] leading-4 text-ink-faint">
              {unauthorized
                ? 'Chats you start here are listed on this device.'
                : 'Start chatting and it will show up here.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
